// ──────────────── Payment / ledger types ────────────────
// Mirrors the backend's OrderUserLedger model + the active-transaction
// response shape. Used by the premium store, polling composables, and
// the StripePayment / ShoppingCart state machines.

export const PaymentStatus = Object.freeze({
  PENDING: "pending",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
  CANCELED: "canceled",
  REQUIRES_ACTION: "requires_action",
  PARTIALLY_FUNDED: "partially_funded",
} as const);
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

export const TransactionType = Object.freeze({
  PAY: "pay",
  REFUND: "refund",
} as const);
export type TransactionType = (typeof TransactionType)[keyof typeof TransactionType];

export const RefundReason = Object.freeze({
  CUSTOMER_EDIT: "customer_edit",
  HUB_EDIT: "hub_edit",
  DASHBOARD_REFUND: "dashboard_refund",
  CANCELLATION: "cancellation",
  INTERNAL_ERROR: "internal_error",
} as const);
export type RefundReason = (typeof RefundReason)[keyof typeof RefundReason];

/**
 * One row of the order_user_ledgers table as returned by the API. The site
 * consumes a subset of the columns; everything else is opaque.
 */
export interface OrderUserLedger {
  id: string;
  order_id: number;
  customer_id: string;
  transaction_type: TransactionType;
  payment_status: PaymentStatus;
  payment_intent_id: string | null;
  refund_intent_id: string | null;
  refund_batch_id: string | null;
  refund_batch_size: number | null;
  customer_delta_net: number;
  customer_delta_vat: number;
  customer_delta_gross: number;
  org_delta_net: number;
  org_delta_vat: number;
  org_delta_gross: number;
  stripe_intent_amount: number;
  stripe_confirmed_amount: number;
  invalidated_at: string | null;
  confirmation_started_at: string | null;
  /**
   * Snapshot of the cart at ledger creation time. Used by the
   * modify-order page to restore the customer's cart against the
   * exact state the backend committed to paying for (rather than the
   * possibly-newer orderProducts on the order itself).
   * Shape: `[{ product_id, amount, modifiers: [...] }, ...]`.
   */
  cart_order_products_snapshot: Array<{
    product_id: string | number;
    amount: number;
    modifiers?: Array<Record<string, unknown>>;
  }> | null;
  created_at: string;
  updated_at: string;
}

/**
 * Aggregated state of a refund batch as returned by the active-transaction
 * endpoint. The site polls `all_confirmed` / `any_failed` on the modify
 * + cancel flows — never the individual ledger statuses.
 */
export interface RefundBatch {
  refund_batch_id: string | null;
  batch_size: number;
  confirmed_count: number;
  failed_count: number;
  all_confirmed: boolean;
  any_failed: boolean;
  ledgers: Array<{
    id: string;
    payment_status: PaymentStatus;
    confirmation_started_at: string | null;
    refund_intent_id: string | null;
    stripe_intent_amount: number;
  }>;
}

/**
 * Response shape for `GET /customers/orders/payments/active-transaction/{id}`.
 *
 * `pay_ledger` is the newest *unresolved* PAY (pending / failed / requires_action)
 * — a SUCCEEDED ledger is not "active" because Single orders allow further edits
 * once payment lands. `refund_ledger` is present only while a batch is in flight.
 */
export interface ActiveOrderTransaction {
  active: boolean;
  pay_ledger: OrderUserLedger | null;
  pay_payment_intent: { id: string; status: string; client_secret: string | null } | null;
  pay_client_secret: string | null;
  stripe_payment_intent_status: string | null;
  refund_ledger: OrderUserLedger | null;
  refund_batch: RefundBatch | null;
  has_unresolved_pay_ledger: boolean;
}

/**
 * Response shape for `GET /customers/orders/payments/active-ledger/{order_id}/{ledger_type}`.
 *
 * Lighter than `ActiveOrderTransaction` — no Stripe PaymentIntent fetch, no
 * dual-ledger aggregation. Used by polling composables where we want a
 * single ledger + its batch state at high frequency.
 *
 * `batch` is populated only when `ledger_type` is `"refund"`.
 */
export interface ActiveLedgerResponse {
  active: boolean;
  ledger: OrderUserLedger | null;
  has_unresolved_pay_ledger: boolean;
  batch: RefundBatch | null;
}

/**
 * Response shape for the public `pay-ledger-status/{payment_intent_id}` endpoint.
 * Status-only (no order details) so a guest can poll without seeing other
 * customers' data.
 */
export interface PayLedgerStatusResponse {
  payment_status: PaymentStatus | null;
}

/**
 * Pointer kept in sessionStorage by `useActiveCheckout` so a tab refresh
 * mid-payment can resume polling against the same PaymentIntent.
 */
export interface ActiveCheckoutPointer {
  orderId: string | number;
  paymentIntentId: string;
  clientSecret: string;
  initiatedAt: number;
}

/**
 * Frontend-side payment status enum the cart store derives from
 * `ActiveOrderTransaction`. Drives every blocker in the UI: "fresh" means
 * the cart is editable, "pending" / "refundPending" disable inputs, etc.
 *
 * Re-exported from `app/utils/premium/constants.ts` to avoid duplication —
 * keep this type in sync with that const.
 */
export type UserOrderPaymentStatus =
  | "fresh"
  | "awaitingPaymentDetails"
  | "pending"
  | "failed"
  | "complete"
  | "refundPending";
